import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';
import { selectDealersList } from '../../reducers/dealersListReducer';
import { DealersBandBar, Section, Title, Button } from './DealersBand.styles';
import MapIcon from '../../assets/icons/MapIcon';
import { GeoJSONProperties } from '../../hooks/useDealershipList';
import { device } from '../../styles/devices';
import { MARKET } from '../../constants/market';
import { PlaceResultType } from '../../types/placeResultLocation';
import DealerName from './DealerName';
import { selectAppConfig } from '../../reducers/appConfigReducer';

interface DealersBandProps {
  onModalOpen: () => void;
}

const DealersBand: React.FC<DealersBandProps> = ({ onModalOpen }) => {
  const { t } = useTranslation();
  const isMobile = !useMediaQuery({ query: device.tablet });
  const { centerLocation, selectedDealersIds, radius, allDealersList } =
    useSelector(selectDealersList);
  const radiusUnit = MARKET === 'CAN' ? 'km' : 'miles';

  const { isHidden } = useSelector(selectAppConfig);

  const getButtonText = useMemo(() => {
    if (!centerLocation) {
      return t('dealerLocationSearch.dealersBand.noLocation');
    }

    const { formattedAddress, name } = centerLocation as PlaceResultType;

    if (!selectedDealersIds.length) {
      return t('dealerLocationSearch.dealersBand.radiusSearch', {
        userLocationName: name || formattedAddress,
        selectedRadius: radius,
        radiusUnit,
      });
    }

    if (selectedDealersIds.length === 1) {
      const dealerInfo = (
        allDealersList as GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties>
      ).features.find((dealer) => dealer.properties.id === selectedDealersIds[0]);

      if (!dealerInfo) return t('dealerLocationSearch.dealersBand.noLocation');

      return <DealerName name={dealerInfo.properties.name} />;
    }

    return t('dealerLocationSearch.dealersBand.multiDealer', {
      selectedDealerCount: selectedDealersIds.length,
    });
  }, [allDealersList, centerLocation, radius, radiusUnit, selectedDealersIds, t]);

  return (
    <DealersBandBar hidden={isHidden}>
      <Section>
        <MapIcon size={isMobile ? 21 : 28} />
        <Title>{t('dealerLocationSearch.dealersBand.locationLabel')}</Title>
        <Button onClick={onModalOpen} data-testid="dealer-search-modal-open-button">
          {getButtonText}
        </Button>
      </Section>
    </DealersBandBar>
  );
};

export default DealersBand;
